/**
 * @file look-size.ts
 * @description Числа группы «Ширина и высота» секции v3 (phase19.16, партия
 * SB120, этап 1 «секция-герой»; site-builder.md SB120; проект
 * docs/phases/phase19.16-materials/uiux-sb120-stage1.md «Дополнение» §2.1,
 * §2.4, §2.8; данные db-design-sb120-stage1.md §1).
 *
 * Чистые функции: режимы высоты реестром (новая схема — запись), расчёт
 * «1280 ÷ 1,78 = 720 px» для «По фото», рамка кадра для ПК и телефона
 * (ширина блока × высота; телефон — 360 × высота телефона или 4:5),
 * «Содержимое ≈ 1140 px», когда положение содержимого не на что двигать.
 */

import {
  DEFAULT_SITE_THEME,
  SECTION_LOOK_LIMITS,
  SECTION_PREVIEW_FRAME,
  type SectionHeight,
  type SectionLook,
  type SectionPlace,
  type SiteSpacingDensity,
  type SiteTheme,
} from "@chaiberi/shared";

import { isOutsideFrame } from "./look-outside";
import { formatPx, SITE_SIDE_PX } from "./look-space";

export type HeightKind = SectionHeight["kind"];
export type Device = "desktop" | "phone";

/** Экран ПК для пересчётов «Экран %» (uiux §2.1: 1280×800). */
export const DESKTOP_SCREEN = { width: 1280, height: 800 } as const;
/** «По кадру телефона 4:5» (вопрос 7, решение 2026-10-18). */
export const PHONE_FRAME_ASPECT = 0.8;

/** Режимы высоты: подпись, умолчание при выборе. Порядок — порядок карточек. */
export const HEIGHT_MODES: ReadonlyArray<{ kind: HeightKind; label: string; make: () => SectionHeight }> = [
  { kind: "content", label: "По содержимому", make: () => ({ kind: "content" }) },
  { kind: "photo", label: "По фото", make: () => ({ kind: "photo" }) },
  { kind: "number", label: "Числом", make: () => ({ kind: "number", value: 600 }) },
  { kind: "screen", label: "Экран", make: () => ({ kind: "screen", value: 100 }) },
];

/** Умолчания чисел режимов — «↺» поля возвращает их. */
export const HEIGHT_DEFAULTS = { number: 600, screen: 100 } as const;

/** Ширина блока на ПК для расчётов, px: своё число, сетка 1280 или окно. */
export function blockWidthPx(container: SectionLook["container"]): number {
  return typeof container === "number" ? container : DESKTOP_SCREEN.width;
}

/** «1,78» — пропорции с запятой, до сотых. */
export function formatAspect(aspect: number): string {
  return String(Math.round(aspect * 100) / 100).replace(".", ",");
}

/** Поле сетки по ширине экрана (globals.css `.px-section`): 22 до 768, 40 с 768, 70 с 1024. */
export function gridSidePx(screen: number): number {
  return screen >= 1024 ? SITE_SIDE_PX[2] : screen >= 768 ? SITE_SIDE_PX[1] : SITE_SIDE_PX[0];
}

/** Расчёт высоты «По фото», как у витрины: число px и подпись. */
export interface PhotoHeightCalc {
  px: number;
  text: string;
}

/**
 * Высота «По фото» — как считает витрина (согласовано 2026-10-18). Без
 * подложки фон во всю ширину окна при любой ширине блока: «на экране 1280:
 * 1280 ÷ 1,78 ≈ 719 px» (телефон — 360). С подложкой — ширина подложки ÷
 * пропорция, ширина подложки = min(окно, ширина блока) − 2 × поле сетки
 * (22 / 40 / 70 × плотность; при «Во всю» полей нет): «(1280 − 140) ÷ 1,78
 * ≈ 640 px», телефон «(360 − 44) ÷ 0,8 = 395 px». В обоих случаях это нижняя
 * граница: текст растит блок. `null` — пропорции неизвестны.
 */
export function photoHeightCalc(
  look: Pick<SectionLook, "container" | "card">,
  device: Device,
  aspect: number | null,
  density: SiteSpacingDensity,
): PhotoHeightCalc | null {
  if (!aspect || aspect <= 0) return null;
  const screen = device === "phone" ? SECTION_PREVIEW_FRAME.phone.width : DESKTOP_SCREEN.width;
  const sign = (v: number) => (Math.abs(v - Math.round(v)) < 0.05 ? "=" : "≈");
  if (!look.card.on) {
    const px = screen / aspect;
    return { px, text: `на экране ${screen}: ${screen} ÷ ${formatAspect(aspect)} ${sign(px)} ${Math.round(px)} px` };
  }
  const block = typeof look.container === "number" ? Math.min(screen, look.container) : screen;
  const sides = look.container === "full" ? 0 : 2 * gridSidePx(screen) * density;
  const width = Math.max(0, block - sides);
  const px = width / aspect;
  const minus = sides > 0 ? `(${block} − ${formatPx(sides)})` : String(block);
  return { px, text: `${minus} ÷ ${formatAspect(aspect)} ${sign(px)} ${Math.round(px)} px` };
}

/** Высота в px на экране; `null` — по содержимому (или неизвестно). */
export function heightPx(height: SectionHeight, device: Device, width: number, fileAspect: number | null): number | null {
  const screen = device === "desktop" ? DESKTOP_SCREEN.height : SECTION_PREVIEW_FRAME.phone.height - SECTION_PREVIEW_FRAME.phone.header;
  switch (height.kind) {
    case "number":
      return height.value;
    case "screen":
      return (screen * height.value) / 100;
    case "photo": {
      const aspect = height.aspect ?? fileAspect;
      return aspect ? width / aspect : null;
    }
    default:
      return null;
  }
}

/**
 * Пропорции рамки кадра (ширина ÷ высота) для шага «Кадр под блок» и рамки
 * «░ уйдёт за край»: ПК — ширина блока × высота (по содержимому — 1280×704,
 * как в проекте), телефон — 360 × высота телефона (по содержимому — 4:5).
 */
export function frameAspect(look: Pick<SectionLook, "container" | "height" | "heightPhone">, device: Device, fileAspect: number | null): number {
  if (device === "desktop") {
    const width = blockWidthPx(look.container);
    const h = heightPx(look.height, "desktop", width, fileAspect);
    return width / (h && h > 0 ? h : 704);
  }
  const width = SECTION_PREVIEW_FRAME.phone.width;
  const h = heightPx(look.heightPhone ?? look.height, "phone", width, fileAspect);
  return h && h > 0 ? width / h : PHONE_FRAME_ASPECT;
}

/**
 * «Содержимое ≈ 1140 px: по 70 px по бокам» — подсказка под шириной. Рамка
 * сайта «По центру» без фактического выхода за неё (`isOutsideFrame`)
 * ограничивает блок: 1920 в рамке 1600 → (1600 − 2 × поле) (QA SB120 этап 2).
 */
export function contentWidthHint(
  look: Pick<SectionLook, "container" | "space" | "outsideFrame" | "bg" | "card">,
  density: SiteSpacingDensity,
  frame: Pick<SiteTheme["frame"], "mode" | "maxWidth"> = DEFAULT_SITE_THEME.frame,
): string {
  if (look.container === "full") return "Во всю ширину — без предела и полей по бокам (см. «Отступы»).";
  const site = SITE_SIDE_PX[2] * density;
  const left = look.space.innerLeft ?? site;
  const right = look.space.innerRight ?? site;
  const framed = frame.mode === "boxed" && !isOutsideFrame(look, frame);
  const block = framed ? Math.min(blockWidthPx(look.container), frame.maxWidth) : blockWidthPx(look.container);
  const content = Math.max(0, block - left - right);
  const sides = left === right ? `по ${formatPx(left)} px по бокам` : `${formatPx(left)} и ${formatPx(right)} px по бокам`;
  return `Содержимое ≈ ${Math.round(content)} px: ${sides} (см. «Отступы»).`;
}

/** Положение по умолчанию: по центру и по центру (section.css, `mx-auto`). */
export const PLACE_DEFAULT: Required<SectionPlace> = { y: "center", x: "center" };

/** Ширина «Во всю» — двигать по горизонтали некуда. */
export function placeXDisabled(look: Pick<SectionLook, "container">): boolean {
  return look.container === "full";
}

/** Высота «По содержимому» — двигать по вертикали некуда. */
export function placeYDisabled(height: SectionHeight): boolean {
  return height.kind === "content";
}

/** Пределы чисел высоты по режиму. */
export function heightRange(kind: "number" | "screen"): { min: number; max: number } {
  return kind === "number" ? SECTION_LOOK_LIMITS.heightPx : SECTION_LOOK_LIMITS.heightScreen;
}

/**
 * Видимая часть фото при `object-fit: cover` по пропорции места и масштабу
 * кадра — доли от фото (0–1): рамка «░ уйдёт за край» (uiux §2.3, §2.4).
 */
export function visibleRect(focus: { x: number; y: number }, frame: { aspect: number; photoAspect: number; zoom?: number }) {
  const z = Math.max(1, frame.zoom ?? 1);
  const wide = frame.photoAspect > frame.aspect;
  const w = (wide ? frame.aspect / frame.photoAspect : 1) / z;
  const h = (wide ? 1 : frame.photoAspect / frame.aspect) / z;
  return { left: (1 - w) * (focus.x / 100), top: (1 - h) * (focus.y / 100), width: w, height: h };
}

